import React from "react";
import { Calendar, Tag, Layers, CheckCircle2, XCircle, Clock, ChevronDown, ChevronUp } from "lucide-react";

const ActivityHeader = ({
  activity,
  isSelected,
  onSelectToggle,
  isExpanded = false,
  onExpandToggle,
  fromSource = "Professor",
}) => {
  const isHodView = fromSource === "HOD";

  const fmtDate = (dStr) => {
    if (!dStr) return "—";
    try {
      return new Date(dStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return dStr;
    }
  };

  // Find latest Faculty reaction and HOD reaction
  const facultyReaction = (activity?.reactions || []).filter((r) => r.from_source !== "HOD").pop();
  const hodReaction = (activity?.reactions || []).filter((r) => r.from_source === "HOD").pop();

  const formatSubroleName = (subrole) => {
    if (!subrole) return "Faculty";
    const map = {
      ASSISTANT_PROFESSOR: "Assistant Professor",
      ASSOCIATE_PROFESSOR: "Associate Professor",
      PROFESSOR: "Professor",
      HEAD_OF_DEPARTMENT: "HOD",
      PROFESSOR_HOD: "HOD",
      SENIOR_RESIDENT: "Senior Resident",
      DEAN: "Dean",
    };
    if (map[subrole]) return map[subrole];
    let cleaned = subrole.replace(/Head of Department/gi, "HOD").replace(/\(HOD\)/gi, "HOD").trim();
    if (cleaned === "Professor + HOD") return "HOD";
    return cleaned;
  };

  const renderFacultyBadge = () => {
    if (!facultyReaction) {
      return (
        <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold text-[11px] border border-slate-200">
          <Clock className="w-3.5 h-3.5 text-slate-500" /> Pending Faculty Review
        </span>
      );
    }

    const isApp = facultyReaction.type === "✅" || facultyReaction.decision === "Approved";
    const facName = facultyReaction.from || facultyReaction.professorEmail || "Faculty";
    const facSubrole = formatSubroleName(facultyReaction.subrole);

    if (isApp) {
      return (
        <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-900 font-bold text-[11px] border border-emerald-300">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Approved by {facSubrole} : {facName}
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-100 text-rose-900 font-bold text-[11px] border border-rose-300">
        <XCircle className="w-3.5 h-3.5 text-rose-600" /> Rejected by {facSubrole} : {facName}
      </span>
    );
  };

  const renderHodBadge = () => {
    if (!hodReaction) {
      return (
        <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 font-bold text-[11px] border border-amber-300 shadow-xs">
          <Clock className="w-3.5 h-3.5 text-amber-700 animate-pulse" /> Pending HOD Review
        </span>
      );
    }

    const isApp = hodReaction.type === "✅" || hodReaction.decision === "Approved";
    const hodName = hodReaction.from || hodReaction.professorEmail || "HOD";
    const hodSubrole = formatSubroleName(hodReaction.subrole || "Head of Department");

    if (isApp) {
      return (
        <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-900 font-bold text-[11px] border border-emerald-300 shadow-xs">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Approved by {hodSubrole} : {hodName}
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-100 text-rose-900 font-bold text-[11px] border border-rose-300 shadow-xs">
        <XCircle className="w-3.5 h-3.5 text-rose-600" /> Rejected by {hodSubrole} : {hodName}
      </span>
    );
  };

  const renderStandardStatusBadge = (status) => {
    const s = (status || "PENDING").toUpperCase();
    if (s === "APPROVED") {
      return (
        <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-bold text-[11px]">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Approved
        </span>
      );
    }
    if (s === "REJECTED") {
      return (
        <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-100 text-rose-800 font-bold text-[11px]">
          <XCircle className="w-3.5 h-3.5 text-rose-600" /> Rejected
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-bold text-[11px]">
        <Clock className="w-3.5 h-3.5 text-amber-600" /> Pending Review
      </span>
    );
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 select-none">
      <div className="flex items-start gap-3 flex-1 cursor-pointer" onClick={onExpandToggle}>
        {onSelectToggle && fromSource !== "Principal" && fromSource !== "ReadOnly" && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelectToggle(activity.id);
            }}
            onClick={(e) => e.stopPropagation()}
            className="mt-1 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
          />
        )}

        <div className="flex-1">
          <h3 className="font-bold text-slate-900 text-base leading-snug hover:text-blue-600 transition-colors">
            {activity.title}
          </h3>

          <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-slate-500 font-medium">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5 text-slate-400" />
              {fmtDate(activity.date)}
            </span>

            {activity.type && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold border border-blue-100">
                <Tag className="w-3 h-3" /> {activity.type}
              </span>
            )}

            {(activity.attempt_type || activity.attemptType) && (
              <span className="flex items-center gap-1 text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                <Layers className="w-3.5 h-3.5 text-slate-400" /> Attempt: {activity.attempt_type || activity.attemptType}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {isHodView ? (
          <>
            {renderFacultyBadge()}
            {renderHodBadge()}
          </>
        ) : (
          <>
            {renderStandardStatusBadge(activity.status)}
            {hodReaction && (
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-100 text-purple-900 font-bold text-[11px] border border-purple-300">
                <CheckCircle2 className="w-3.5 h-3.5 text-purple-600" /> HOD Verified
              </span>
            )}
          </>
        )}

        {/* Expand / Collapse Toggle Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onExpandToggle && onExpandToggle();
          }}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border font-bold text-xs transition-all ${
            isExpanded
              ? "bg-slate-200 text-slate-800 border-slate-300 hover:bg-slate-300"
              : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 shadow-sm"
          }`}
          title={isExpanded ? "Hide Details" : "View Details"}
        >
          <span>{isExpanded ? "Hide Details" : "View Details"}</span>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-slate-700" />
          ) : (
            <ChevronDown className="w-4 h-4 text-blue-600" />
          )}
        </button>
      </div>
    </div>
  );
};

export default ActivityHeader;
